import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';

export type ChatConversationItem = {
  partner: {
    id: string;
    name?: string;
    avatar?: string;
    email?: string;
    phone?: string;
    role?: string;
    location?: string;
    timezone?: string;
  };
  last_message?: {
    id: string;
    sender_id: string;
    receiver_id: string;
    message: string;
    is_read: boolean;
    sender_role: string;
    sender_name: string;
    sender_avatar?: string;
    timestamp: string;
    is_delivered: boolean;
    attachment: any;
    type: string;
  };
  unread_count?: number;
};

type ChatListState = {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  chats: ChatConversationItem[];
};

const initialState: ChatListState = {
  loading: false,
  error: false,
  errorMessage: '',
  chats: [],
};

const normalizeChatList = (payload: any): ChatConversationItem[] => {
  const data = payload?.response ?? payload?.data ?? payload ?? [];
  if (!Array.isArray(data)) {
    return [];
  }
  
  // Handle two possible formats:
  // Format 1: { partner: {...}, last_message: {...}, unread_count: ... }
  // Format 2: { id, partner_id, partner_name, partner_type, user_avatar, last_message, last_message_time, unread_count }
  
  return data.filter(item => {
    // Check if item has either partner.id or partner_id
    return item?.partner?.id || item?.partner_id;
  }).map(item => {
    // If the item already has the correct structure with 'partner' object
    if (item.partner?.id) {
      return {
        partner: item.partner,
        last_message: item.last_message,
        unread_count: item.unread_count ?? 0,
      };
    }
    
    // Otherwise, transform the flat structure to nested structure
    return {
      partner: {
        id: item.partner_id || item.id,
        name: item.partner_name,
        avatar: item.user_avatar,
        role: item.partner_type,
      },
      last_message: item.last_message ? {
        id: '',
        sender_id: '',
        receiver_id: '',
        message: item.last_message,
        is_read: true,
        sender_role: '',
        sender_name: '',
        timestamp: item.last_message_time,
        is_delivered: true,
        attachment: null,
        type: 'text',
      } : undefined,
      unread_count: item.unread_count ?? 0,
    };
  });
};

const resolveErrorMessage = (payload: any, fallback: string) => {
  if (!payload) {
    return fallback;
  }
  if (typeof payload === 'string') {
    return payload;
  }
  if (typeof payload?.data?.message === 'string') {
    return payload.data.message;
  }
  if (typeof payload?.data?.error === 'string') {
    return payload.data.error;
  }
  if (typeof payload?.message === 'string') {
    return payload.message;
  }
  return fallback;
};

export const getChatConversations = createAsyncThunk<
  ChatConversationItem[],
  void,
  { rejectValue: { status?: number; message?: string; data?: any } }
>('chatList/getConversations', async (_, thunkAPI) => {
  try {
    const { data } = await api.get('/api/chats/conversations');
    return normalizeChatList(data);
  } catch (error: any) {
    // Extract only serializable parts of the error
    return thunkAPI.rejectWithValue({
      status: error?.response?.status || error?.status,
      message: error?.response?.data?.message || error?.message || 'Failed to load chat list.',
      data: error?.response?.data,
    });
  }
});

const chatListSlice = createSlice({
  name: 'chatList',
  initialState,
  reducers: {
    resetChatList: (state) => {
      state.loading = false;
      state.error = false;
      state.errorMessage = '';
      state.chats = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getChatConversations.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(getChatConversations.fulfilled, (state, action) => {
      state.loading = false;
      state.chats = action.payload;
    });
    builder.addCase(getChatConversations.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = resolveErrorMessage(
        action.payload ?? action.error,
        'Failed to load chat list.'
      );
      state.chats = [];
    });
  },
});

export const { resetChatList } = chatListSlice.actions;
export default chatListSlice.reducer;
